import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import logo from '../assets/logo-centrao.png';
import { mascaraTelefone, soDigitos, telefoneValido } from '../helpers/mascaras';

// Tela de PRIMEIRO ACESSO — mostrada logo após o login quando
// `user.senha_provisoria === true`. Bloqueia o resto do portal até o arquiteto:
//   1. confirmar/informar e-mail
//   2. confirmar/informar WhatsApp (com máscara)
//   3. criar uma senha pessoal
// E-mail e telefone vêm pré-preenchidos (do portal ou do ERP) pra só confirmar.
export default function TrocarSenhaObrigatorio() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', telefone: '', nova_senha: '', confirmar: '' });
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);

  // Busca sugestões de e-mail/telefone pra pré-preencher
  useEffect(() => {
    api.get('/arquitetos/primeiro-acesso/dados')
      .then(r => {
        setForm(f => ({
          ...f,
          email: r.data?.email_sugerido || '',
          telefone: r.data?.telefone_sugerido ? mascaraTelefone(r.data.telefone_sugerido) : '',
        }));
      })
      .catch(() => {})
      .finally(() => setCarregando(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');

    const emailTrim = form.email.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setErro('E-mail inválido. Confira ou deixe em branco.');
      return;
    }
    if (form.telefone && !telefoneValido(form.telefone)) {
      setErro('WhatsApp incompleto. Informe DDD + número (ex: (33) 99999-9999).');
      return;
    }
    if (form.nova_senha.length < 6) {
      setErro('A nova senha deve ter ao menos 6 caracteres.');
      return;
    }
    if (form.nova_senha !== form.confirmar) {
      setErro('A confirmação não coincide com a nova senha.');
      return;
    }

    setSalvando(true);
    try {
      await api.post('/arquitetos/primeiro-acesso', {
        email: emailTrim || null,
        telefone: soDigitos(form.telefone) || null,
        nova_senha: form.nova_senha,
      });
      // Refaz o login pra obter token sem a flag senha_provisoria
      await login(user.username, form.nova_senha);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao concluir o primeiro acesso.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #14301C 0%, #1F6A2D 45%, #1F6A2D 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <div className="card p-4 p-md-5" style={{ borderTop: '4px solid #E2901A' }}>
          <div className="text-center mb-4">
            <img src={logo} alt="Centrão" style={{ height: 56, objectFit: 'contain', marginBottom: 12 }} />
            <h4 style={{ color: '#1F6A2D', fontWeight: 700, marginBottom: 6 }}>Bem-vindo(a) ao seu portal!</h4>
            <p style={{ color: '#777', fontSize: '0.88rem', marginBottom: 0 }}>
              Olá, <strong>{user?.nome}</strong>. É seu primeiro acesso — confirme seus dados de contato e crie sua senha pessoal.
            </p>
          </div>

          {erro && (
            <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: '0.88rem' }}>
              <i className="bi bi-exclamation-circle me-2"></i>{erro}
            </div>
          )}

          {carregando ? (
            <div className="text-center py-4">
              <div className="spinner-border" style={{ color: '#1F6A2D' }}></div>
            </div>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>
                E-mail <span style={{ color: '#aaa', fontWeight: 400 }}>(para recuperar senha)</span>
              </label>
              <input
                type="email"
                className="form-control"
                placeholder="seu@email.com"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>
                WhatsApp <span style={{ color: '#aaa', fontWeight: 400 }}>(DDD + número)</span>
              </label>
              <input
                type="tel"
                inputMode="numeric"
                className="form-control"
                placeholder="(33) 99999-9999"
                value={form.telefone}
                onChange={e => setForm({ ...form, telefone: mascaraTelefone(e.target.value) })}
              />
            </div>
            <hr style={{ margin: '18px 0', opacity: 0.5 }} />
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>Crie sua senha</label>
              <input
                type="password"
                className="form-control"
                placeholder="Mínimo 6 caracteres"
                value={form.nova_senha}
                onChange={e => setForm({ ...form, nova_senha: e.target.value })}
                minLength={6}
                required
              />
            </div>
            <div className="mb-4">
              <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>Confirmar senha</label>
              <input
                type="password"
                className="form-control"
                placeholder="Repita a senha"
                value={form.confirmar}
                onChange={e => setForm({ ...form, confirmar: e.target.value })}
                required
              />
            </div>

            <button
              type="submit"
              className="btn-primary-arq w-100"
              disabled={salvando}
              style={{ fontSize: '1rem', padding: '12px' }}
            >
              {salvando
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                : <><i className="bi bi-shield-check me-2"></i>Salvar e entrar</>
              }
            </button>
          </form>
          )}

          <div className="text-center mt-3">
            <button
              type="button"
              onClick={logout}
              style={{ background: 'none', border: 'none', color: '#666', fontSize: '0.82rem', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Sair
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
